/**
 * FeedbackModal - Bug report and feature request submission
 * 
 * Collects user feedback with auto-captured metadata
 */

import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useProject } from '../../context/ProjectContext'
import { submitFeedback } from '../../services/feedback'
import { Icon } from './Icon'
import './FeedbackModal.css'

/**
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {function} props.onClose - Close handler
 */
export function FeedbackModal({ isOpen, onClose }) {
  const { user } = useAuth()
  const { project, projectId } = useProject()
  
  const [type, setType] = useState('bug')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!title.trim()) {
      setError('Please enter a title')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      await submitFeedback({
        type,
        title: title.trim(),
        description: description.trim(),
        userEmail: user?.email,
        userId: user?.uid,
        currentUrl: window.location.href,
        userAgent: navigator.userAgent,
        projectId,
        projectName: project?.name,
      })

      setSubmitted(true)
      // Reset form after short delay
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (err) {
      console.error('Feedback submission error:', err)
      setError(err.message || 'Failed to submit feedback')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    // Reset state
    setType('bug')
    setTitle('')
    setDescription('')
    setSubmitting(false)
    setSubmitted(false)
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="feedback-modal__overlay" onClick={handleClose}>
      <div className="feedback-modal" onClick={e => e.stopPropagation()}>
        <button className="feedback-modal__close" onClick={handleClose}>
          <Icon name="close" size={20} />
        </button>

        {submitted ? (
          <div className="feedback-modal__success">
            <Icon name="check" size={48} />
            <h2>Thank you!</h2>
            <p>Your feedback has been submitted.</p>
          </div>
        ) : (
          <>
            <h2 className="feedback-modal__title">Send Feedback</h2>
            <p className="feedback-modal__subtitle">
              Help us improve Plot Planet
            </p>

            <form onSubmit={handleSubmit}>
              {/* Type selector */}
              <div className="feedback-modal__type-selector">
                <button
                  type="button"
                  className={`feedback-modal__type-btn ${type === 'bug' ? 'feedback-modal__type-btn--active' : ''}`}
                  onClick={() => setType('bug')}
                >
                  <Icon name="warning" size={16} />
                  Bug report
                </button>
                <button
                  type="button"
                  className={`feedback-modal__type-btn ${type === 'feature' ? 'feedback-modal__type-btn--active' : ''}`}
                  onClick={() => setType('feature')}
                >
                  <Icon name="sparkles" size={16} />
                  Feature request
                </button>
              </div>

              {/* Title */}
              <div className="feedback-modal__field">
                <label className="feedback-modal__label">
                  {type === 'bug' ? 'What went wrong?' : 'What would you like to see?'}
                </label>
                <input
                  type="text"
                  className="feedback-modal__input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={type === 'bug' 
                    ? 'e.g., Timeline doesn\'t load on mobile'
                    : 'e.g., Add dark mode option'
                  }
                  autoFocus
                />
              </div>

              {/* Description */}
              <div className="feedback-modal__field">
                <label className="feedback-modal__label">
                  Details (optional)
                </label>
                <textarea
                  className="feedback-modal__textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={type === 'bug'
                    ? 'Steps to reproduce, what you expected to happen...'
                    : 'How would this help your workflow?'
                  }
                  rows={4}
                />
              </div>

              {/* Error message */}
              {error && (
                <div className="feedback-modal__error">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="feedback-modal__actions">
                <button
                  type="button"
                  className="feedback-modal__btn feedback-modal__btn--secondary"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="feedback-modal__btn feedback-modal__btn--primary"
                  disabled={submitting || !title.trim()}
                >
                  {submitting ? 'Submitting...' : 'Submit feedback'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

export default FeedbackModal
